import { Roles } from '@common/types/user';
import { IOSSwitch } from '@components/utility/switches';
import { SignInGuestIcon } from '@event/assets/icons/SignInGuestIcon';
import { useEventDashboardContext } from '@event/contexts/EventContext';
import { EventDetailTabs, EventFormikType } from '@event/types/event';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CreateIcon from '@mui/icons-material/Create';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, FormControlLabel, Typography } from '@mui/material';
import { PrefixedLink } from '@utils/route';
import { useFormikContext } from 'formik';
import { ChangeEvent, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

interface IEventDetailViewActionBarProps {
    onEditClick: () => void;
    onPublishClick: () => void;
    onUnpublishClick: () => void;
    isSaving: boolean;
}

const EventDetailViewActionBar = ({
    onEditClick,
    onPublishClick,
    onUnpublishClick,
    isSaving,
}: IEventDetailViewActionBarProps) => {
    const {
        event,
        user,
        cohostPermissions,
        signInGuestDialogState: [_, setSignInGuestDialogState],
        tabState: [tab, setTab],
    } = useEventDashboardContext();
    const { values, errors, handleChange } = useFormikContext<EventFormikType>();
    const { type, eventId } = useParams();

    const isOrganizer = useMemo(() => event?.owner?.id === user?.id, [event?.owner, user?.id]);
    const isCohost = useMemo(
        () => event?.organizers?.some((organizer) => organizer.user.id === user?.id),
        [event?.organizers, user?.id]
    );
    const isAdmin = useMemo(() => user?.role === Roles.VICTRON, [user?.role]);
    const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);
    const isPublished = useMemo(() => values.published, [values.published]);

    const permissions = useMemo(
        () => ({
            edit: (() => {
                // Is the user the organizer?
                if (isOrganizer || isAdmin) {
                    return true;
                } else {
                    // Is the user the co-host?
                    if (isCohost) {
                        // Does the user have "Edit" Permissions?
                        return !!cohostPermissions?.find((perm) => perm.role === user?.role)?.edit;
                    } else {
                        return false;
                    }
                }
            })(),
            signIn: (() => {
                if (user?.id === event?.owner?.id || isAdmin) {
                    return false;
                } else {
                    if (
                        event?.organizers?.some((organizer) => organizer.user.id === user?.id) &&
                        !!cohostPermissions?.find((perm) => perm.role === user?.role)?.add_guests
                    ) {
                        return false;
                    } else {
                        return true;
                    }
                }
            })(),
            publish: (() => {
                if (isOrganizer || isAdmin) {
                    return true;
                } else {
                    if (isCohost) {
                        return !!cohostPermissions?.find((perm) => perm.role === user?.role)?.publish;
                    } else {
                        return false;
                    }
                }
            })(),
        }),
        [cohostPermissions, isAdmin, isCohost, isOrganizer, user?.role, event?.owner?.id, event?.organizers]
    );

    const handlePublishToggle = async (e: ChangeEvent) => {
        const newValue = (e.target as HTMLInputElement).checked;

        if (!values.published && newValue) {
            if (!isSaving) {
                onPublishClick();
            }
        } else if (values.published && !newValue) {
            handleChange(e);
            onUnpublishClick();
        }
    };

    const handleSignInClick = () => {
        setSignInGuestDialogState('list');
        if (![EventDetailTabs.OVERVIEW, EventDetailTabs.AUDIENCE].includes(tab)) {
            setTab(EventDetailTabs.AUDIENCE);
        }
    };

    return (
        <>
            <Button
                component={Link}
                color="secondary-alt"
                startIcon={<ArrowBackRoundedIcon />}
                to="/app/dashboard/events"
                className="rounded-full black-border"
            >
                <Typography fontWeight={500} fontSize={15} letterSpacing={0.46}>
                    Return to overview
                </Typography>
            </Button>
            <div className="flex items-center gap-2">
                <FormControlLabel
                    disabled={!permissions.publish}
                    className="gap-2"
                    control={
                        <IOSSwitch
                            size="small"
                            name="published"
                            color={hasErrors ? 'error' : 'success'}
                            checked={values?.published ?? false}
                            onChange={handlePublishToggle}
                        />
                    }
                    label={
                        <Typography fontWeight={700} fontSize={16} letterSpacing={0}>
                            Publish
                        </Typography>
                    }
                />
                <PrefixedLink to={`/events/${type}/${eventId}${!isPublished ? '?preview=true' : ''}`} target="_blank">
                    <Button
                        color="warning-alt"
                        startIcon={<OpenInNewIcon />}
                        className="w-[174px] rounded-full black-border"
                    >
                        <Typography fontWeight={500} fontSize={15} letterSpacing={0.46}>
                            Show event
                        </Typography>
                    </Button>
                </PrefixedLink>
                <Button
                    startIcon={<CreateIcon />}
                    onClick={onEditClick}
                    className="w-[174px] black-border rounded-full"
                    disabled={!permissions.edit}
                >
                    <Typography fontWeight={500} fontSize={15} letterSpacing={0.46}>
                        Edit
                    </Typography>
                </Button>
                <Button
                    startIcon={<SignInGuestIcon size={20} />}
                    disabled={permissions.signIn}
                    className="w-[174px] black-border rounded-full"
                    onClick={handleSignInClick}
                >
                    <Typography fontWeight={500} fontSize={15} letterSpacing={0.46}>
                        Sign In Guests
                    </Typography>
                </Button>
            </div>
        </>
    );
};

export { EventDetailViewActionBar };
